import { useState } from "react";
import { Check, Package, RotateCcw } from "lucide-react";

import { useResetEnvironment, useResetTeamMachine, useSandboxSizes } from "@/shared/api/agents";
import { ago } from "@/shared/lib/time";
import { ListCard, ListRow, ListTile } from "@/shared/components/ListCard";
import { Note, Row, confirmDangerCls, confirmDialogCls } from "@/shared/components/settings-atoms";
import { GhostButton } from "@/shared/components/ui-kit";
import {
  AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import { Switch } from "@/shared/components/ui/switch";
import SandboxSecrets from "./SandboxSecrets";
import Section from "./Section";

import type { ConfigSection } from "../ConfigSectionHelp";
import type { Agent, SandboxEnvironment, SandboxSize } from "@/shared/types";

const BASE_IMAGE = "Debian · Python 3.11 · Node 22 · git";

function SandboxSpecGrid({ size, environment }: { size: SandboxSize; environment: SandboxEnvironment | null }) {
  // One column: on a phone, half a row cannot fit "Kept  30 days after last use"
  const specs: [string, string][] = [
    ["CPU", `${size.cpu} vCPU`],
    ["Memory", `${size.memory_gib} GiB`],
    ["Disk", `${size.disk_gib} GiB`],
    ["Kept", "30 days after last use"],
    ["Image", environment ? environment.note : BASE_IMAGE],
  ];
  return (
    <span className="fade-in mt-2.5 block max-w-[420px] rounded-[8px] ring-1 ring-[var(--hairline)] overflow-hidden">
      <span className="grid gap-[1px] bg-[var(--hairline)]">
        {specs.map(([k, v]) => (
          <span
            key={k}
            className="flex items-center justify-between gap-3 min-w-0 px-2.5 h-[26px] bg-[var(--surface)]"
          >
            <span className="shrink-0 section-label">{k}</span>
            <span className="text-[12px] font-mono tabular-nums text-[var(--text-secondary)] truncate" title={v}>{v}</span>
          </span>
        ))}
      </span>
    </span>
  );
}

function SandboxRow({ size, selected, environment, onSelect }: {
  size: SandboxSize;
  selected: boolean;
  environment: SandboxEnvironment | null;
  onSelect: () => void;
}) {
  return (
    <button
      onClick={onSelect}
      aria-pressed={selected}
      className={`press group w-full flex items-start gap-3 px-4 py-3 text-left min-w-0 ${selected ? "bg-[var(--accent-hover)]" : "hover:bg-[var(--accent-hover)]"}`}
    >
      <span className={`shrink-0 mt-[2px] w-4 h-4 rounded-full flex items-center justify-center transition-colors ${
        selected ? "bg-[var(--brand)]" : "ring-1 ring-inset ring-[var(--hairline-strong)] group-hover:ring-[var(--hairline-focus)]"
      }`}>
        {selected && <Check className="w-[9px] h-[9px] text-[var(--brand-foreground)] fade-in" strokeWidth={3.5} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] font-medium tracking-[-0.005em] truncate text-[var(--text-primary)]">{size.name}</span>
        {selected ? (
          <SandboxSpecGrid size={size} environment={environment} />
        ) : (
          <span className="block mt-[1px] text-[12.5px] text-[var(--text-tertiary)] truncate">
            <span className="font-mono tabular-nums">{`${size.cpu} vCPU · ${size.memory_gib} GiB · ${size.disk_gib} GiB disk`}</span>
          </span>
        )}
      </span>
    </button>
  );
}

/* Asks before something that can't be undone from here. */
function Confirm({ open, onOpenChange, title, says, act, onAct }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  says: string;
  act: string;
  onAct: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={`${confirmDialogCls} max-w-[420px]`}>
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[15px] tracking-[-0.01em]">{title}</AlertDialogTitle>
          <AlertDialogDescription className="text-[13px] leading-[1.6]">{says}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full text-[13px]">Cancel</AlertDialogCancel>
          <button className={confirmDangerCls} onClick={() => { onOpenChange(false); onAct(); }}>{act}</button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* What every machine of the agent starts from. Definable builds it — the product has no
   terminal — so this shows it and can put the base image back. */
function EnvironmentCard({ agent }: { agent: Agent }) {
  const environment = agent.capabilities?.sandboxEnvironment ?? null;
  const reset = useResetEnvironment();
  const [confirming, setConfirming] = useState(false);
  return (
    <>
      <ListCard
        label="Environment"
        action={environment && (
          <GhostButton onClick={() => setConfirming(true)} className="h-7 px-2.5">
            <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.75} /> Reset to base
          </GhostButton>
        )}
      >
        <ListRow
          lead={<ListTile><Package className="w-[14px] h-[14px]" strokeWidth={1.75} /></ListTile>}
          title={environment ? environment.note : "Base image"}
          desc={environment
            ? `Saved by Builder ${ago(environment.saved_at)} · ${environment.size}`
            : `${BASE_IMAGE}. Ask Builder to install what this agent's chats need, once for all of them.`}
        />
      </ListCard>
      <Confirm
        open={confirming}
        onOpenChange={setConfirming}
        title="Reset to the base image?"
        says="Once you publish, new machines start from the base image. To get this environment back, Builder builds it again."
        act="Reset"
        onAct={() => reset.mutate(agent.id)}
      />
    </>
  );
}

/* The team's chats and schedules on one machine; everyone else keeps one each. */
function TeamMachineCard({ agent, onUpdateAgent }: {
  agent: Agent;
  onUpdateAgent: (updater: (a: Agent) => Agent) => void;
}) {
  const shared = !!agent.capabilities?.sandboxShared;
  const reset = useResetTeamMachine();
  const [confirming, setConfirming] = useState(false);
  const share = (on: boolean) => onUpdateAgent((a) => ({ ...a, capabilities: { ...a.capabilities, sandboxShared: on } }));
  return (
    <>
      <ListCard label="Team machine">
        <Row
          className="px-4"
          title="Your team shares one machine"
          desc="Chats from your workspace and scheduled tasks work on one machine that keeps its files between them. People on channels or the API keep a machine each."
        >
          <Switch checked={shared} onCheckedChange={share} aria-label="Your team shares one machine" />
        </Row>
        {shared && (
          <Row className="px-4" title="Reset team machine" desc="Delete it: the next chat starts a new one from the published environment.">
            <GhostButton onClick={() => setConfirming(true)}>Reset</GhostButton>
          </Row>
        )}
      </ListCard>
      <Confirm
        open={confirming}
        onOpenChange={setConfirming}
        title="Reset the team machine?"
        says="Everything your team's chats and scheduled tasks left on it is deleted. Files people attached or the agent shared come back."
        act="Reset"
        onAct={() => reset.mutate(agent.id)}
      />
    </>
  );
}

/* A Daytona machine per chat, kept between messages: its size, what it starts from, the
   agent's secrets, and whether the team shares one. */
export default function SandboxSection({ agent, onUpdateAgent, onHelp }: {
  agent: Agent;
  onUpdateAgent: (updater: (a: Agent) => Agent) => void;
  onHelp: (section: ConfigSection) => void;
}) {
  const { data: sandboxSizes } = useSandboxSizes();
  const enabled = !!agent.capabilities?.sandboxEnabled;
  // null is the backend's default size
  const sandboxSize = agent.capabilities?.sandboxSize ?? "small";
  const environment = agent.capabilities?.sandboxEnvironment ?? null;
  const toggle = () => onUpdateAgent((a) => ({ ...a, capabilities: { ...a.capabilities, sandboxEnabled: !enabled } }));
  const pick = (id: string) => onUpdateAgent((a) => ({ ...a, capabilities: { ...a.capabilities, sandboxSize: id === "small" ? null : id } }));

  return (
    <Section section="sandbox" onHelp={onHelp} enabled={enabled} onToggle={toggle}>
      <div className="space-y-4">
        <ListCard label="Size" action={<span className="text-[12px] text-[var(--text-tertiary)] whitespace-nowrap">Powered by Daytona</span>}>
          {(sandboxSizes ?? []).map((size) => (
            <SandboxRow key={size.id} size={size} selected={size.id === sandboxSize} environment={environment} onSelect={() => pick(size.id)} />
          ))}
        </ListCard>
        <EnvironmentCard agent={agent} />
        <SandboxSecrets agentId={agent.id} />
        <TeamMachineCard agent={agent} onUpdateAgent={onUpdateAgent} />
      </div>
      <Note>
        A chat's machine is kept between messages: it stops when idle and is deleted 30 days after
        its last use. A new size or environment reaches a chat on its next message.
      </Note>
    </Section>
  );
}
